import "./DealManagement.scss";
import { Tabs, Card, Row, Col, Tag, Typography, Pagination } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, useMemo, useCallback } from "react";
import { allDealsList } from "../../redux/slices/DealReqSlice";
import { useNavigate } from "react-router-dom";
import { UploadOutlined } from "@ant-design/icons";
import CustomButton from "../../components/CustomButton/Button";
import { formatCurrency, DateFormatter } from "../../helpers/helpers";
import { DEAL_CARD_PAGE_SIZE } from "../../constants/tableConfig";
import { count } from "../../redux/slices/DealSlice";

const { Title, Text } = Typography;

const DealManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dealList } = useSelector((state) => state?.dealReq);
  const { countData } = useSelector((state) => state?.deals);

  const [activeKey, setActiveKey] = useState("1");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let statusId = undefined;
    switch (activeKey) {
      case "1":
        statusId = undefined;
        break;
      case "2":
        statusId = "DSC1000001";
        break;
      case "3":
        statusId = "DSC1000002";
        break;
      case "4":
        statusId = "DSC1000012";
        break;
      default:
        statusId = undefined;
        break;
    }
    dispatch(allDealsList({ statusId, dealCategoryId: "DCT1000002" }));
  }, [dispatch, activeKey]);

  useEffect(() => {
    dispatch(count({ type: "DEALS", dealCategoryId: "DCT1000002" }));
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeKey]);

  const handleTabChange = useCallback((key) => {
    setActiveKey(key);
  }, []);

  const renderCard = useCallback(
    (deal) => (
      <Card className="deal-card" bordered={false}>
        <div className="card-header">
          <Text className="deal-title">
            {deal?.dealName || "Havih Biofuel Project"}
          </Text>
          <Tag className="status-tag">{deal?.dealStatus || "Pending"}</Tag>
        </div>

        <div className="deal-category">
          {deal?.dealCategory || "Joint Ventures"}
        </div>

        <div className="amount-section">
          <Text className="label">Deal Amount</Text>
          <div className="deal-amount">
            {deal?.dealValue ? formatCurrency(deal?.dealValue) : "₹0"}
          </div>
        </div>

        <div className="details-grid">
          <div className="detail-row">
            <span className="label">Deal ID</span>
            <span className="value">{deal?.dealId || "26050C"}</span>
          </div>
          <div className="detail-row">
            <span className="label">Company</span>
            <span className="value">
              {deal?.orgName || "Aaroghya Private Limited"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Submitted on</span>
            <span className="value">
              {deal?.createdAt ? DateFormatter(deal?.createdAt) : "-"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Expected ROI</span>
            <span className="value">
              {deal?.roi ? `${deal.roi}%` : "14%"}
            </span>
          </div>
          <div className="detail-row">
            <span className="label">Project Duration</span>
            <span className="value">
              {deal?.dealDuration || deal?.tenure || "-"} Months
            </span>
          </div>
        </div>
      </Card>
    ),
    [],
  );

  const paginatedDeals = useMemo(() => {
    const start = (currentPage - 1) * DEAL_CARD_PAGE_SIZE;
    return (dealList ?? []).slice(start, start + DEAL_CARD_PAGE_SIZE);
  }, [dealList, currentPage]);

  const dealGrid = useMemo(
    () => (
      <>
        <Row gutter={[16, 16]}>
          {paginatedDeals.map((deal) => (
            <Col xs={24} sm={24} md={12} lg={8} key={deal.dealId}>
              {renderCard(deal)}
            </Col>
          ))}
        </Row>
        {(dealList?.length ?? 0) > DEAL_CARD_PAGE_SIZE && (
          <Pagination
            current={currentPage}
            pageSize={DEAL_CARD_PAGE_SIZE}
            total={dealList?.length ?? 0}
            onChange={setCurrentPage}
            style={{ marginTop: 16, textAlign: "center" }}
            showSizeChanger={false}
          />
        )}
      </>
    ),
    [paginatedDeals, dealList?.length, currentPage, renderCard],
  );

  const tabCountMap = useMemo(() => {
    return (countData || [])
      .filter((item) => item.dealCategoryId === "DCT1000002")
      .reduce((acc, item) => {
        acc[item.statusName.toLowerCase()] = Number(item.count) || 0;
        return acc;
      }, {});
  }, [countData]);

  const totalCount = useMemo(
    () => Object.values(tabCountMap).reduce((sum, count) => sum + count, 0),
    [tabCountMap],
  );

  const tabItems = useMemo(
    () => [
      {
        key: "1",
        label: `All Deals (${totalCount})`,
        children: dealGrid,
      },
      {
        key: "2",
        label: `Pending (${tabCountMap.pending || 0})`,
        children: dealGrid,
      },
      {
        key: "3",
        label: `Approved (${tabCountMap.approved || 0})`,
        children: dealGrid,
      },
      {
        key: "4",
        label: `Rejected (${tabCountMap.rejected || 0})`,
        children: dealGrid,
      },
    ],
    [dealGrid, totalCount, tabCountMap],
  );

  return (
    <div className="deal-management">
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Deal Management</Title>
          <Text type="secondary">Track and manage all your deals in one place</Text>
        </Col>
        <Col>
          <CustomButton
            data-testid="deal-request-button"
            type="primary"
            icon={<UploadOutlined />}
            onClick={() => navigate("/deal-request")}
            style={{ height: 40 }}
          >
            Deal Request
          </CustomButton>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="1"
        activeKey={activeKey}
        onChange={handleTabChange}
        items={tabItems}
      />
    </div>
  );
};

export default DealManagement;
